/* eslint-disable class-methods-use-this */
import * as basicLightbox from 'basiclightbox';
import { disableScroll, enableScroll } from '../../global/helpers';

export default class ImageLightbox {
	closeButtonClass = 'basicLightbox__close';

	closeButtonEventAdded = false;

	constructor(el) {
		this.el = el;
		this.image = this.el.querySelector('img');
		this.addImageLinkClass();

		this.galleryItem = this.el.closest('.blocks-gallery-item');
		if (this.galleryItem) {
			this.allGalleryItems = this.galleryItem.parentElement.children;
			this.setPrevNextItems();
		}

		this.el.addEventListener('click', this.handleClick);
	}

	getImageLinkClass() {
		return 'enlarge';
	}

	addImageLinkClass() {
		if (this.image) {
			this.el.classList.add(this.getImageLinkClass());
		}
	}

	getLightbox() {
		const html = this.getHTML();
		return html ? basicLightbox.create(html, this.getOptions()) : false;
	}

	getHTML() {
		const innerHTML = this.getInnerHTML();
		return innerHTML ? `${this.getCloseHTML()}${innerHTML}` : false;
	}

	getCloseHTML() {
		return `<button tabindex="1" class="${this.closeButtonClass} basicLightbox__button no-button-style">
			<span class="screen-reader-text">Close</span>
			<span class="dashicons dashicons-no-alt"></span>
		</button>`;
	}

	getPrevHtml() {
		if (this.prevGalleryItem) {
			return `<button class="basicLightbox__prev">
				<span class="screen-reader-text">Close</span>
				<span class="dashicons dashicons-arrow-left-alt"></span>
			</button>`;
		}
		return '';
	}

	getNextHtml() {
		if (this.nextGalleryItem) {
			return `<button class="basicLightbox__next">
				<span class="screen-reader-text">Close</span>
				<span class="dashicons dashicons-arrow-right-alt"></span>
			</button>`;
		}
		return '';
	}

	getInnerHTML() {
		// Define our src and alt attributes to use in the <img /> tag
		const attrSrc = this.el.getAttribute('href');
		const attrAlt = this.image ? this.image.getAttribute('alt') : '';
		return `<div class="basicLightbox__image">
			${this.getPrevHtml()}
			<img src="${attrSrc}" alt="${attrAlt}" />
			${this.getNextHtml()}
		</div>`;
	}

	getOptions() {
		return {
			onShow: () => {
				this.handleOnShow();
			},
			onClose: () => {
				this.handleOnClose();
			},
		};
	}

	/**
	 * Update the lightbox image using the gallery item passed.
	 *
	 * @param {node} item Gallery item to use for updating the lightbox image.
	 */
	updateLightBoxImage(item = false) {
		const galleryItem = item || this.galleryItem;
		if (galleryItem) {
			const link = galleryItem.querySelector('a');
			const image = galleryItem.querySelector('img');
			if (link && image) {
				this.lightboxImage.setAttribute('src', link.href);
				this.lightboxImage.setAttribute('alt', image.alt);
				this.setPrevNextItems(galleryItem);
			}
		}
	}

	/**
	 * Set the prev and next items relative to the passed in item. If no item is
	 * passed, then this will fall back to this.galleryItem set in constructor().
	 *
	 * @param {node} item Gallery item to set the prev and next items relative to.
	 */
	setPrevNextItems(item = false) {
		const galleryItem = item || this.galleryItem;
		if (galleryItem) {
			this.prevGalleryItem = galleryItem.previousElementSibling || this.allGalleryItems[this.allGalleryItems.length - 1];
			this.nextGalleryItem = galleryItem.nextElementSibling || this.allGalleryItems[0];
		}
	}

	/**
	 * Reset the gallery lightbox back to its initial state.
	 */
	resetGallery() {
		this.updateLightBoxImage();
		this.setPrevNextItems();
	}

	/**
	 * Handle image link click that opens the lightbox.
	 */
	handleClick = (e) => {
		this.handleClickCallback(e);
	}

	handleClickCallback = (e) => {
		this.lightbox = this.getLightbox();
		if (this.lightbox) {
			e.preventDefault();
			this.lightboxEl = this.lightbox.element();
			this.lightboxImage = this.lightboxEl.querySelector('img');

			if (this.galleryItem) {
				this.lightboxEl.addEventListener('click', this.handlePrevNext);
			}

			this.lightbox.show();
		}
	}

	/**
	 * Handle lightbox open.
	 */
	handleOnShow = () => {
		// Get the close button inside the lightbox and add a click event tht will close the lightbox.
		const closeButton = this.lightboxEl.querySelector(`.${this.closeButtonClass}`);
		// Add click event to close button that closes the lightbox.
		closeButton.addEventListener('click', () => {
			this.lightbox.close();
		});

		document.documentElement.classList.add('lightbox-open');
		disableScroll();
		this.afterOnShow();
	}

	/**
	 * Handle lightbox close.
	 */
	handleOnClose = () => {
		document.documentElement.classList.remove('lightbox-open');
		enableScroll();

		// Reset the image gallery back to its initial state.
		if (this.galleryItem) {
			// Use timeout in order to avoid image changing before lightbox closes.
			setTimeout(() => {
				this.resetGallery();
			}, 500);
		}

		this.afterOnClose();
	}

	/**
	 * Handle previous and next clicks in image gallery lightboxes.
	 */
	handlePrevNext = (e) => {
		let galleryItem;
		const prev = e.target.closest('.basicLightbox__prev');
		const next = e.target.closest('.basicLightbox__next');
		if (prev) {
			galleryItem = this.prevGalleryItem;
		} else if (next) {
			galleryItem = this.nextGalleryItem;
		}

		if (galleryItem) {
			this.updateLightBoxImage(galleryItem);
		}
	}

	/**
	 * Adds the ability to hook in after the lightbox is shown.
	 * Helpful in child classes.
	 */
	afterOnShow() {}

	/**
	 * Adds the ability to hook in after the lightbox is closed.
	 * Helpful in child classes.
	 */
	afterOnClose() {}
}
